import { useAppDispatch, useAppSelector } from '../../../../contexts/hooks';
import { selectResultsPermissions, selectInvalidationTime, selectInvalidateOnlyOwnAnalitos, selectModuleBarcode, selectModuleAlarms, selectModuleEmail, selectModuleWhatsApp } from '../../../../contexts/selectors';
import DataGrid, { Column, Editing, Grouping, KeyboardNavigation, Paging, Scrolling, Selection, Sorting } from 'devextreme-react/data-grid';
import DynamicEditCellComponent from './components/DynamicEditCellComponent';
import CellComponent from './components/CellComponent';
import CustomStore from 'devextreme/data/custom_store';

import { useApiFunctionsResult } from './Functions/Results';
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DataSource from 'devextreme/data/data_source';
import { newToast, NoDataPanel } from '../../..';
import { UseApiResults } from '../../../../api/api.results';
import { ResultEstadoResult, ResultFilterRequest, SysIResultados } from '../../../../types/types.orders';
import { ModalConfirmation } from '../../..';
import { ResultTypeResult } from '../../../../types/types.analitos';
import { KeyDownEvent } from 'devextreme/ui/data_grid';
import { ModalNotes } from '../../../utils/modal-notes/ModalNotes';
import moment from 'moment';
import { getCurrentServerDate } from '../../../../api/api.configuration';
import { showModalNotes, setFilteresToOrders, setFiltersToOrdersText, showModalImagenesDx } from '../../../../contexts/slices/orders';
import { generateFiltersText } from '../../../../utils/functions';
import { LIS_States } from '../../../../utils/systemTypes';
import { shallowEqual } from 'react-redux';
import { ResultsToolbar } from './components/ToolBarComponent';
import { formatMessage } from 'devextreme/localization';
import { useScreenSize } from '../../../../utils/media-query';
import { useActiveTab } from '../../../../hooks/useActiveTab';

// Modales livianos de la pestaña: van en el bundle inicial junto con el panel de órdenes y
// se montan solo mientras están abiertos.
import { ModalResultTemplates } from './modals/ResultTemplates';
import { ModalResultImages } from './modals/ResultImages';
import { ModalResultMultiline } from './modals/ResultMultiline';
import { ModalOpportunity } from './modals/Opportunity';
import { SendMailResuls } from './modals/SendMailResuls';
import { SendWhatsAppResults } from './modals/SendWhatsAppResults';
import { ModalSampleTubes } from './components/ModalSampleTubes';

// Modales diferidos: arrastran librerías pesadas (motor de reportes, visor DICOM, exceljs)
// que no deben viajar en el bundle inicial. webpackPrefetch hace que el navegador los
// descargue en segundo plano apenas la app está ociosa, así que al abrirlos ya están en caché.
const ModalMicrobiologyResults = lazy(() => import(/* webpackPrefetch: true */ './modals/ResultMicrobiology').then((m) => ({ default: m.ModalMicrobiologyResults })));
const ModalResultImagenesDx = lazy(() => import(/* webpackPrefetch: true */ './modals/ResultImagenesDx').then((m) => ({ default: m.ModalResultImagenesDx })));
const ModalResultHistory = lazy(() => import(/* webpackPrefetch: true */ './modals/ResultHistory').then((m) => ({ default: m.ModalResultHistory })));
const ModalReportResults = lazy(() => import(/* webpackPrefetch: true */ '../../reporting/ModalReportResults').then((m) => ({ default: m.ModalReportResults })));
const ModalReportPreview = lazy(() => import(/* webpackPrefetch: true */ '../../reporting/ModalReportPreview').then((m) => ({ default: m.ModalReportPreview })));

const Index = () => {
  const [modalConfirmation, setModalConfirmation] = useState<{ visible: boolean; message: string }>({ visible: false, message: '' });
  const [modalOpportunityVisible, setModalOpportunityVisible] = useState<boolean>(false);
  const [modalConfirmationDeleteAnalito, setModalConfirmationDeleteanalito] = useState<boolean>(false);
  const [modalPrintResult, setModalPrintResult] = useState<{ visible: boolean; studies?: number[] | undefined }>({ visible: false });
  const [modalSendMail, setModalSendMail] = useState<{ visible: boolean; studies?: number[] | undefined }>({ visible: false });
  const [modalSendWhatsApp, setModalSendWhatsApp] = useState<{ visible: boolean; studies?: number[] | undefined }>({ visible: false });
  const [modalPreviewResult, setModalPreviewResult] = useState<boolean>(false);
  const [modalSampleTubesVisible, setModalSampleTubesVisible] = useState<boolean>(false);
  const [refreshAfterCloseModal, setRefreshAfterCloseModal] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const [modalNotePlantillaInvalidation, setModalNotePlantillaInvalidation] = useState<{ visible: boolean; currentNote?: string }>({ visible: false, currentNote: '' });
  const { handleValRef, handleHistory, handleNote, handleSample, handleStatusResult, handleAnt, handlePreview2, handlePreview1, checkAlarmAndAburd, checkAlarmAndAburdNoLabel, isNotEmpty, handlePrintBarcodesLabel } = useApiFunctionsResult();
  const { getResults, reSendResults, putResult, validateResult, inValidateResult, putInValidationNote, deleteAnalitoResult } = UseApiResults();

  // Hook para verificar si esta tab está activa (tab 2 = Results)
  const isActiveTab = useActiveTab(2);

  const idOrd = useAppSelector((x) => x.globalOrders.CurrentOrder.IdOrder, shallowEqual) || -1;
  const dataModal = useAppSelector((x) => x.globalOrders.CurrentOrder.ModalResultTemplate);
  const dataModalMicro = useAppSelector((x) => x.globalOrders.CurrentOrder.ModalResultMicrobiology);
  const dataModalMultiline = useAppSelector((x) => x.globalOrders.CurrentOrder.ModalResultMultiline);
  const dataModalImagenesDx = useAppSelector((x) => x.globalOrders.CurrentOrder.ModalResultImagenesDx);
  // Visibilidad de los modales que leen su estado de Redux: se necesita aquí para montarlos
  // solo mientras están abiertos.
  const modalImagesVisible = useAppSelector((x) => x.globalOrders.CurrentOrder.ModalResultImage?.Visible ?? false);
  const modalHistoryVisible = useAppSelector((x) => x.globalOrders.CurrentOrder.ModalHistory?.Visible ?? false);

  // Selector usando FiltersToOrders.Estados en lugar de ResultsFilters.selectedStates
  const selectedStates = useAppSelector((x) => {
    try {
      return x.globalOrders?.FiltersToOrders?.Estados || [];
    } catch (error) {
      console.warn('Error accessing FiltersToOrders:', error);
      return [];
    }
  }, shallowEqual);

  // shallowEqual: el fallback `|| []` crea un arreglo nuevo en cada evaluación; sin
  // comparación por contenido cada render disparaba una recarga del grid.
  const selectedAreas = useAppSelector((x) => x.globalOrders?.FiltersToOrders?.Areas || [], shallowEqual);
  const selectedStudies = useAppSelector((x) => x.globalOrders?.FiltersToOrders?.Estudios || [], shallowEqual);

  // Selector para obtener el estado actual de FiltersToOrders
  const currentFiltersToOrders = useAppSelector((x) => x.globalOrders?.FiltersToOrders, shallowEqual);

  const { isSmall, isXSmall, isLarge } = useScreenSize();
  const currentIdUser = useAppSelector((x) => x.userInfo.User?.Us_Id, shallowEqual);
  const currentLocation = useAppSelector((x) => x.userInfo.Location, shallowEqual);
  const currentOrdLocation = useAppSelector((x) => x.globalOrders.CurrentOrder.OrderData?.Ord_Sd_Id, shallowEqual);
  const modeEditor = useAppSelector((x) => x.configuration.IsEditing, shallowEqual);
  const ModalNotesValidation = useAppSelector((x) => x.globalOrders.CurrentOrder.ModalNoteVal);

  // Selectores consolidados optimizados (reduce de 12+ selectores a 2)
  const permissions = useAppSelector(selectResultsPermissions);
  const InvalidationTime = useAppSelector(selectInvalidationTime);
  const InvalidateOnlyOwrAnalitos = useAppSelector(selectInvalidateOnlyOwnAnalitos);
  const ModuleBarcode = useAppSelector(selectModuleBarcode);
  const ModuleAlarms = useAppSelector(selectModuleAlarms);
  const ModuleEmail = useAppSelector(selectModuleEmail);
  const ModuleWhatsApp = useAppSelector(selectModuleWhatsApp);

  // Aliases para compatibilidad con código existente
  const Prm_Conf_Campos_Diseno = permissions.canDesignFields;
  const Prm_Res_DelAn = permissions.canDeleteAnalito;
  const Prm_ResImprimir = permissions.canPrintResults;
  const Prm_CodBarImprimir = permissions.canPrintBarcodes;
  const Prm_Res_Val_OtrSd = permissions.canValidateOtherLocations;
  const Prm_Res_InVal_OtrSd = permissions.canInvalidateOtherLocations;
  const Prm_Res_Retransmitir = permissions.canRetransmit;
  const Prm_Res_Conf = permissions.canSeeConfidential;
  const Prm_ResValidar = permissions.canValidate;
  const Prm_ResDesvalidar = permissions.canInvalidate;
  const Prm_Res_Val_Crit = permissions.canValidateCritical;
  const Prm_Res_Mail = permissions.canSendEmail;
  const Prm_Res_WhatsApp = permissions.canSendWhatsApp;
  // Listas usadas por generateFiltersText para mantener en sync los chips del panel derecho
  const LstEstudios = useAppSelector((x) => x.configuration.Data?.Estudios);
  const LstAreas = useAppSelector((x) => x.configuration.Data?.Areas);
  const LstServicios = useAppSelector((x) => x.configuration.Data?.ComboBox)?.filter((x) => x.Name === 'SysA_Servicios')[0]?.Data;
  const LstSedes = useAppSelector((x) => x.configuration.Data?.ComboBox)?.filter((x) => x.Name === 'SysI_Sedes')[0]?.Data;
  //const [joinedGroup, setJoinedGroup] = useState<string>('');
  const gridRef = useRef<DataGrid>(null);
  const isPrintingRef = useRef<boolean>(false);
  //const { runApi } = useApi();

  //const { handleSaveDesign, handleLoadDesign } = useApiOrders();

  // Todo lo que necesita la carga del grid se lee por ref en el momento de cargar. Así el
  // CustomStore y el DataSource se crean UNA sola vez: antes se recreaban con cada cambio
  // de pestaña/orden/filtro, y el DataGrid recargaba por su cuenta al recibir un dataSource
  // nuevo, además de los refresh() explícitos. Eran hasta 3 peticiones por cambio de pestaña.
  const loadCtxRef = useRef({ isActiveTab, idOrd, selectedStates, selectedAreas, selectedStudies, currentFiltersToOrders, getResults, putResult });
  loadCtxRef.current = { isActiveTab, idOrd, selectedStates, selectedAreas, selectedStudies, currentFiltersToOrders, getResults, putResult };
  // Petición de resultados en vuelo, para abortarla cuando se va a pedir otra.
  const abortRef = useRef<AbortController | null>(null);

  const customData = useMemo(() => {
    return new CustomStore({
      key: 'Res_Id',
      load: () => {
        const ctx = loadCtxRef.current;
        // Solo cargar datos si el tab está activo y hay un idOrd válido
        if (!ctx.isActiveTab || ctx.idOrd === -1) {
          return Promise.resolve({
            data: [],
            totalCount: 0,
            summary: [],
            groupCount: 0,
          });
        }

        const validStates = Array.isArray(ctx.selectedStates) ? ctx.selectedStates : [];
        const data: ResultFilterRequest = {
          states: validStates,
          areas: ctx.selectedAreas,
          studies: ctx.selectedStudies,
          tracking: ctx.currentFiltersToOrders?.Tracking,
        };
        const controller = new AbortController();
        abortRef.current = controller;
        return ctx
          .getResults(undefined, data, controller.signal)
          .then((res) => {
            return {
              data: res,
              totalCount: res.totalCount,
              summary: res.summary,
              groupCount: res.groupCount,
            };
          })
          .catch(() => {
            throw new Error('Error de carga de datos');
          })
          .finally(() => {
            if (abortRef.current === controller) {
              abortRef.current = null;
            }
          });
      },
      update: (key, values) => {
        return loadCtxRef.current
          .putResult({ keys: [key], Result: values.Res_Resultado })
          .then((res) => {
            if (res) {
              gridRef.current?.instance.refresh();
            }
          })
          .catch(() => {
            throw new Error('Error actualizando el analito');
          });
      },
    });
  }, []);

  useEffect(() => {
    if (dataModal && dataModal?.Visible) {
      setRefreshAfterCloseModal(true);
    }

    if (dataModal && !dataModal.Visible && dataModal.Update && refreshAfterCloseModal) {
      gridRef?.current?.instance.refresh();
      setRefreshAfterCloseModal(false);
    }
  }, [dataModal]);

  useEffect(() => {
    if (dataModalMicro && dataModalMicro?.Visible) {
      setRefreshAfterCloseModal(true);
    }

    if (dataModalMicro && !dataModalMicro.Visible && dataModalMicro.Update && refreshAfterCloseModal) {
      gridRef?.current?.instance.refresh();
      setRefreshAfterCloseModal(false);
    }
  }, [dataModalMicro]);

  useEffect(() => {
    if (dataModalMultiline && dataModalMultiline?.Visible) {
      setRefreshAfterCloseModal(true);
    }

    if (dataModalMultiline && !dataModalMultiline.Visible && dataModalMultiline.Update && refreshAfterCloseModal) {
      gridRef?.current?.instance.refresh();
      setRefreshAfterCloseModal(false);
    }
  }, [dataModalMultiline]);

  const handleCloseImagenesDx = useCallback(
    (update: boolean) => {
      dispatch(showModalImagenesDx({ Visible: false, An_Id: -1, Res_Id: -1, An_Nombre: '', Res_Estado: ResultEstadoResult.Validado, Res_Resultado: '', Update: update, Pc_Id: -1 }));
      if (update) {
        gridRef?.current?.instance.refresh();
      }
    },
    [dispatch]
  );

  const customDataSrc = useMemo(() => {
    return new DataSource({
      store: customData,
      reshapeOnPush: false,
    });
  }, [customData]);

  // Si al montarse la pestaña ya está activa con una orden válida, el propio DataGrid hace
  // la primera carga al crearse; el efecto de abajo no debe repetirla.
  const skipFirstRefreshRef = useRef(isActiveTab && idOrd !== -1);

  // ÚNICO disparo de recarga: al activarse la pestaña, al cambiar de orden o al cambiar los
  // filtros. No recarga al desactivarse. Antes de pedir, aborta la petición en vuelo: el
  // DataGrid descarta esa respuesta de todos modos y, como su cola de cargas es secuencial,
  // sin abortar la nueva petición no salía hasta que terminaba la vieja.
  useEffect(() => {
    if (!isActiveTab || idOrd === -1) return;
    if (skipFirstRefreshRef.current) {
      skipFirstRefreshRef.current = false;
      return;
    }
    abortRef.current?.abort();
    gridRef.current?.instance.refresh();
  }, [isActiveTab, idOrd, selectedStates, selectedAreas, selectedStudies]);

  // Al desmontar, no dejar una petición huérfana en vuelo.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const handleKeys = (event: KeyDownEvent) => {
    if (event.event?.key === 'F3') {
      handleClickValidateResult();
      event.event.preventDefault();
    }
  };

  const handleSelectedState = useCallback(
    (e: number[]) => {
      const validStates = Array.isArray(e) ? e : [];

      // Si no hay filtros existentes, crear un objeto básico con Fechas vacías
      const baseFilters = currentFiltersToOrders || { Fechas: [] };

      // Actualizar solo la propiedad Estados en Redux. NO re-consultar las órdenes:
      // el listado del panel derecho se deja intacto y la orden actual permanece en
      // pantalla. El grid de resultados se refresca solo porque selectedStates se
      // deriva de FiltersToOrders.Estados y el useEffect([selectedStates]) ya lo maneja.
      const newFilters = {
        ...baseFilters,
        Estados: validStates,
      };

      dispatch(setFilteresToOrders(newFilters));
      // Sincroniza los chips de filtros del panel derecho
      const filtersText = generateFiltersText(newFilters, LstAreas, LstEstudios, LstServicios, LIS_States, LstSedes);
      dispatch(setFiltersToOrdersText(filtersText));
    },
    [dispatch, currentFiltersToOrders, LstAreas, LstEstudios, LstServicios, LstSedes]
  );

  const updateData = useCallback(() => {
    gridRef.current?.instance.refresh();
  }, []);

  // const loadDesign = useCallback(async () => {
  //   const drLd = await handleLoadDesign();
  //   return drLd;
  // }, []);

  const handleSelectAll = useCallback(() => {
    gridRef.current?.instance.selectAll();
  }, []);

  const handleShowSamples = useCallback(() => {
    setModalSampleTubesVisible(true);
  }, []);

  const handleGroupRowEs = useCallback((rowData) => {
    try {
      return (
        <div
          onClick={() => {
            const itemsToSelect = gridRef.current?.instance.getDataSource().items();
            let items;
            if (rowData.key.length === 2) {
              items = itemsToSelect?.filter((x) => x.key === rowData.key[0])[0].items.filter((x) => x.key === rowData.key[1])[0].items;
            } else {
              items = itemsToSelect?.filter((x) => x.key === rowData.key[0])[0].items;
            }
            gridRef.current?.instance.deselectAll().then(() => {
              gridRef.current?.instance.selectRows(
                items.map((obj) => obj.Res_Id),
                true
              );
            });
          }}>
          {rowData.data.items[0].Estudio.Es_Nombre}
        </div>
      );
    } catch (error) {
      newToast('ERROR', 'Debe agrupar en el orden de columnas: Área > Estudio', 6);
      //gridRef.current?.instance.clearGrouping();
      return <div></div>;
    }
  }, []);

  const handleGroupRowAr = useCallback((rowData) => {
    try {
      const itemRow = rowData.data.items[0].key ? rowData.data.items[0].items[0].Area.Aa_Nombre : rowData.data.items[0].Area.Aa_Nombre;
      return (
        <div
          onClick={() => {
            let items;
            if (rowData.data.items[0].key) {
              items = rowData.data.items.flatMap((subArray) => subArray.items.map((objeto) => objeto.Res_Id));
            } else {
              items = rowData.data.items.map((objeto) => objeto.Res_Id);
            }
            gridRef.current?.instance.deselectAll().then(() => {
              gridRef.current?.instance.selectRows(items, true);
            });
          }}>
          {itemRow}
        </div>
      );
    } catch (error) {
      newToast('ERROR', 'Debe agrupar en el orden de columnas: Área > Estudio', 6);
      gridRef.current?.instance.clearGrouping();
      return <div></div>;
    }
  }, []);

  const handleClearResult = useCallback(async () => {
    try {
      await gridRef.current?.instance.beginCustomLoading('');
      const info = await gridRef.current?.instance.getSelectedRowsData();
      const keys = await info
        ?.filter((x) => x.Res_Estado === 2)
        .map((x) => {
          return x.Res_Id;
        });

      if (!keys || keys.length === 0) {
        newToast('INFO', 'Debe seleccionar un elemento válido para clarear', 4);
        return;
      }

      const result = await putResult({ keys });
      if (result) {
        gridRef.current?.instance.deselectAll();
        gridRef.current?.instance.refresh();
      }
    } finally {
      await gridRef.current?.instance.endCustomLoading();
    }
  }, [putResult]);

  const handlePinResultWhite = useCallback(async () => {
    try {
      if (!Prm_ResValidar) {
        newToast('INFO', 'No cuenta con los permisos para validar resultados', 5);
        return;
      }
      //Validar si tiene permisos de validar resultados de otras sedes.
      if (currentLocation !== currentOrdLocation && !Prm_Res_Val_OtrSd) {
        newToast('INFO', 'No cuenta con los permisos para validar resultados de otras sedes', 5);
        return;
      }

      await gridRef.current?.instance.beginCustomLoading('');
      let info: SysIResultados[] = (await gridRef.current?.instance.getSelectedRowsData()) as SysIResultados[];
      //Eliminamos los resultados que no están en estado 2
      info = info.filter((x) => x.Res_Estado === ResultEstadoResult.SinResultado);
      if (info.length === 0) {
        return;
      }
      let keys = await info.map((x) => {
        return x.Res_Id;
      });

      await validateResult({ keys, action: ResultEstadoResult.ValidadoBlanco });
    } finally {
      await gridRef.current?.instance.endCustomLoading();
      await gridRef.current?.instance.deselectAll();
      await gridRef.current?.instance.refresh();
    }
  }, [currentLocation, currentOrdLocation, validateResult]);

  const handleCombinedActions = useCallback(
    (cellData) => {
      // Estilos en línea para el contenedor
      const containerStyle = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '8px', // Espacio entre los elementos
        width: '100%',
      };

      // Obtener los elementos renderizados por las funciones originales
      const historyElement = handleHistory(cellData);
      const noteElement = handleNote(cellData);

      // Crear un contenedor para ambos elementos con estilos en línea
      return (
        <div style={containerStyle}>
          {historyElement}
          {noteElement}
        </div>
      );
    },
    [handleHistory, handleNote]
  );

  //#region MODAL OPPORTUNITY
  const handleOpenModalOpportunity = useCallback(() => {
    setModalOpportunityVisible(true);
  }, []);

  const handleCloseModalOpportunity = useCallback(() => {
    setModalOpportunityVisible(false);
  }, []);
  //#endregion

  //#region VALIDATION

  const handleconfirmationValidateCriticos = useCallback(
    async (value: boolean) => {
      setModalConfirmation({ visible: false, message: '' });
      let info: SysIResultados[] = (await gridRef.current?.instance.getSelectedRowsData()) as SysIResultados[];
      let keysCriticals: number[] = [];

      let keys = await info
        ?.filter((x) => x.Res_Estado === 2)
        .map((x) => {
          return x.Res_Id;
        });

      if (!value) {
        for (let i = 0; i < info.length; i++) {
          const element = info[i];
          if (element.Analito.An_Tr_Id === ResultTypeResult.NUMERICO || element.Analito.An_Tr_Id === ResultTypeResult.FORMULA) {
            if (checkAlarmAndAburd(element)) {
              keysCriticals.push(element.Res_Id);
            }
          } else if (element.Analito.An_Tr_Id === ResultTypeResult.TEXTUAL || element.Analito.An_Tr_Id === ResultTypeResult.AUTOCOMPLETAR || element.Analito.An_Tr_Id === ResultTypeResult.LISTA) {
            if (element.Res_Resultado?.toUpperCase().trim() === element.Analito?.An_ValCritico?.toUpperCase().trim()) {
              keysCriticals.push(element.Res_Id);
            }
          }
        }
        newToast('INFO', 'Se excluyeron resultados críticos', 4);
        keys = keys.filter((x) => !keysCriticals.includes(x));
      }

      if (!keys || keys.length === 0) {
        newToast('INFO', 'Debe seleccionar un elemento válido para validar', 4);
        return;
      }

      await validateResult({ keys, action: ResultEstadoResult.Validado });
      gridRef.current?.instance.clearSelection();
      gridRef.current?.instance.refresh();
    },
    [checkAlarmAndAburd, validateResult]
  );

  const handleClickValidateResult = useCallback(async () => {
    try {
      if (!Prm_ResValidar) {
        newToast('INFO', 'No cuenta con los permisos para validar resultados', 5);
        return;
      }
      //Validar si tiene permisos de validar resultados de otras sedes.
      if (currentLocation !== currentOrdLocation && !Prm_Res_Val_OtrSd) {
        newToast('INFO', 'No cuenta con los permisos para validar resultados de otras sedes', 5);
        return;
      }

      await gridRef.current?.instance.beginCustomLoading('');
      let info: SysIResultados[] = (await gridRef.current?.instance.getSelectedRowsData()) as SysIResultados[];
      //Eliminamos los resultados que no están en estado 2
      info = info.filter((x) => x.Res_Estado === ResultEstadoResult.ConResultado);
      if (info.length === 0) {
        return;
      }
      let keysCriticals: number[] = [];
      //Validamos si hay resultados confidenciales y si tiene permisos de ver y validar resultados confidenciales

      if (info?.filter((x) => x.Estudio.Es_Confidencial).length > 0 && !Prm_Res_Conf) {
        info = info.filter((x) => !x.Estudio.Es_Confidencial);
      }

      //Luego validamos que no hayan resultados criticos.
      if (ModuleAlarms) {
        for (let i = 0; i < info.length; i++) {
          const element = info[i];
          if (element.Analito.An_Tr_Id === ResultTypeResult.NUMERICO || element.Analito.An_Tr_Id === ResultTypeResult.FORMULA) {
            if (checkAlarmAndAburdNoLabel(element)) {
              keysCriticals.push(element.Res_Id);
            }
          } else if (element.Analito.An_Tr_Id === ResultTypeResult.TEXTUAL || element.Analito.An_Tr_Id === ResultTypeResult.AUTOCOMPLETAR || element.Analito.An_Tr_Id === ResultTypeResult.LISTA) {
            if (element.Res_Resultado?.toUpperCase().trim() === element.Analito?.An_ValCritico?.toUpperCase().trim()) {
              keysCriticals.push(element.Res_Id);
            }
          }
        }
      }

      //Validamos si hay críticos
      if (keysCriticals.length > 0 && ModuleAlarms) {
        if (Prm_Res_Val_Crit) {
          setModalConfirmation({ visible: true, message: 'Desea validar los resultados críticos para los analitos seleccionados' });
          return;
        }
        newToast('WARNING', 'Usted no cuenta con permisos para validar resultados críticos', 4);
        await handleconfirmationValidateCriticos(false);
        return;
      }
      await handleconfirmationValidateCriticos(true);
    } finally {
      await gridRef.current?.instance.endCustomLoading();
    }
  }, [currentLocation, currentOrdLocation, ModuleAlarms, handleconfirmationValidateCriticos, checkAlarmAndAburdNoLabel]);

  const handleConfirValidateCritical = () => {
    handleconfirmationValidateCriticos(true);
  };

  const handleCancelValidateCritical = () => {
    handleconfirmationValidateCriticos(false);
  };

  //#endregion

  //#region INVALIDATION

  const handleOpenModalInvalidation = useCallback(async () => {
    if (!Prm_ResDesvalidar) {
      newToast('INFO', 'No cuenta con los permisos para desvalidar resultados', 5);
      return;
    }
    //Validar si tiene permisos de validar resultados de otras sedes.
    if (currentLocation !== currentOrdLocation && !Prm_Res_InVal_OtrSd) {
      newToast('INFO', 'No cuenta con los permisos para desvalidar resultados de otras sedes', 5);
      return;
    }
    try {
      await gridRef.current?.instance.beginCustomLoading('');
      let info: SysIResultados[] = (await gridRef.current?.instance.getSelectedRowsData()) as SysIResultados[];
      //Eliminamos los resultados que no están en estado > 3
      info = info.filter((x) => x.Res_Estado >= ResultEstadoResult.Validado);
      if (info.length === 0) {
        return;
      }
      setModalNotePlantillaInvalidation({ visible: true, currentNote: '' });
    } finally {
      await gridRef.current?.instance.endCustomLoading();
    }
  }, [currentLocation, currentOrdLocation]);

  const handleNoteValidationConfirm = useCallback(
    async (note: string) => {
      if (!note) {
        newToast('WARNING', 'Debe ingresar una nota de desvalidación', 5);
        return;
      }
      setModalNotePlantillaInvalidation({ visible: false });
      let info: SysIResultados[] = (await gridRef.current?.instance.getSelectedRowsData()) as SysIResultados[];
      //Eliminamos los resultados que no están en estado > 3
      info = info.filter((x) => x.Res_Estado >= ResultEstadoResult.Validado);
      //Añadimos todas las llaves
      let keys = await info.map((x) => {
        return x.Res_Id;
      });
      //Realizamos las demas validaciones y vamos eliminando de claves
      for (let i = 0; i < info.length; i++) {
        const fechaVal = moment(info[i].Res_FechaValidacion);
        const currentDate = moment(await getCurrentServerDate());
        const minutesValidation = currentDate.diff(fechaVal, 'minutes');
        if (minutesValidation > InvalidationTime) {
          //Ha finalizado el tiempo de validación para el elemento seleccionado
          newToast('WARNING', `'${info[i].Analito.An_Nombre}' es demasiado antiguo para ser desvalidado`, 5);
          keys = keys.filter((x) => x !== info[i].Res_Id);
          continue;
        }
        //Validamos si tiene permisos para DesvalidarSoloExamenesPropios
        if (InvalidateOnlyOwrAnalitos && info[i].Res_Us_Id !== currentIdUser) {
          newToast('WARNING', 'Solo podrá desvalidar analitos propios', 5);
          keys = keys.filter((x) => x !== info[i].Res_Id);
        }
      }

      try {
        await gridRef.current?.instance.beginCustomLoading('');
        if (keys.length > 0) {
          await inValidateResult({ keys, note });
        }
      } finally {
        gridRef.current?.instance.clearSelection();
        gridRef.current?.instance.refresh();
        await gridRef.current?.instance.endCustomLoading();
      }
    },
    [InvalidationTime, InvalidateOnlyOwrAnalitos, currentIdUser, inValidateResult]
  );

  const handleNoteValidationCancel = useCallback(async () => {
    setModalNotePlantillaInvalidation({ visible: false });
    await gridRef.current?.instance.clearSelection();
  }, []);

  //#endregion

  //#region NOTAS VALIDACION
  const handleConfirmNotesValidation = useCallback(
    (note: string) => {
      if (!ModalNotesValidation) {
        return;
      }
      dispatch(showModalNotes({ Visible: false, An_Id: -1, Res_Estado: ResultEstadoResult.Validado, Res_Id: -1, Res_Resultado: '', An_Nombre: '', Update: false, Pc_Id: -1 }));
      putInValidationNote(ModalNotesValidation?.Res_Id, note).then((res) => {
        if (res) {
          newToast('SUCCESS', 'Nota registrada correctamente', 5);
          gridRef.current?.instance.refresh();
        }
      });
    },
    [ModalNotesValidation, dispatch, putInValidationNote]
  );

  const handleCancelNotesValidation = useCallback(() => {
    dispatch(showModalNotes({ Visible: false, An_Id: -1, Res_Estado: ResultEstadoResult.Validado, Res_Id: -1, Res_Resultado: '', An_Nombre: '', Update: false, Pc_Id: -1 }));
  }, [dispatch]);
  //#endregion

  //#region Retransmitir resultados

  const handleReSendResulds = useCallback(async () => {
    try {
      if (!Prm_Res_Retransmitir) {
        newToast('WARNING', 'No cuenta con permisos para retransmitir resultados', 5);
        return;
      }
      await gridRef.current?.instance.beginCustomLoading('');
      const info: SysIResultados[] | undefined = await gridRef.current?.instance.getSelectedRowsData();
      if (!info) {
        return;
      }
      const keys = await info
        ?.filter((x) => x.Res_Estado === ResultEstadoResult.OtrasFuentes && x.Res_EnviadoOtrasFuentes)
        .map((x) => {
          return x.Res_Id;
        });

      if (!keys || keys.length === 0) {
        newToast('INFO', 'Debe seleccionar un elemento válido para retransmitir', 5);
        return;
      }

      const result = await reSendResults({ keys });
      if (result) {
        gridRef.current?.instance.deselectAll();
        gridRef.current?.instance.refresh();
      }
    } finally {
      await gridRef.current?.instance.endCustomLoading();
    }
  }, [reSendResults]);
  //#endregion

  //#region IMPRIMIR
  const handlePrintResults = useCallback(async () => {
    if (idOrd === -1) {
      return;
    }
    if (!Prm_ResImprimir) {
      newToast('INFO', 'No cuenta con los permisos para imprimir resultados', 5);
      return;
    }
    let info = await gridRef.current?.instance.getDataSource();
    const data = info?.items().flatMap((area: any) => area.items.flatMap((estudio: any) => estudio.items)) as SysIResultados[];

    const infoFiltered = data.filter((x) => x.Res_Estado >= ResultEstadoResult.Validado);
    if (infoFiltered.length === 0) {
      newToast('INFO', 'No hay analitos validados para la orden seleccionada', 5);
      return;
    }

    setModalPrintResult({ visible: true });
  }, [idOrd]);

  const handlePrintResultsSelected = useCallback(async () => {
    if (idOrd === -1) {
      return;
    }

    if (!Prm_ResImprimir) {
      newToast('INFO', 'No cuenta con los permisos para imprimir resultados', 5);
      return;
    }
    try {
      await gridRef.current?.instance.beginCustomLoading('');
      let info: SysIResultados[] = (await gridRef.current?.instance.getSelectedRowsData()) as SysIResultados[];
      //Eliminamos los resultados que no están en estado 2
      info = info.filter((x) => x.Res_Estado >= ResultEstadoResult.Validado);
      if (info.length === 0) {
        newToast('INFO', 'No hay analitos validados para la orden seleccionada', 5);
        return;
      }
      //Sacamos los estudios seleccionados
      const selectedStudies: number[] = [];
      const uniqueMap: { [key: number]: boolean } = {};

      info.forEach((obj) => {
        if (!uniqueMap[obj.Res_Es_Id]) {
          uniqueMap[obj.Res_Es_Id] = true;
          selectedStudies.push(obj.Res_Es_Id);
        }
      });
      setModalPrintResult({ visible: true, studies: selectedStudies });
    } finally {
      await gridRef.current?.instance.endCustomLoading();
    }
  }, [idOrd]);

  const handlePrintBarcode = useCallback(async () => {
    //Todas las etiquetas
    if (idOrd === -1) {
      return;
    }
    if (!Prm_CodBarImprimir) {
      newToast('INFO', 'No cuenta con los permisos para imprimir etiquetas', 5);
      return;
    }

    // Prevenir doble clic
    if (isPrintingRef.current) {
      return;
    }

    try {
      isPrintingRef.current = true;
      await handlePrintBarcodesLabel(idOrd);
    } finally {
      setTimeout(() => {
        isPrintingRef.current = false;
      }, 500);
    }
  }, [idOrd, handlePrintBarcodesLabel, Prm_CodBarImprimir]);

  const handlePrintBarcodeSelected = useCallback(async () => {
    //Todas las etiquetas seleccionadas.
    if (idOrd === -1) {
      return;
    }
    if (!Prm_CodBarImprimir) {
      newToast('INFO', 'No cuenta con los permisos para imprimir etiquetas', 5);
      return;
    }

    // Prevenir doble clic
    if (isPrintingRef.current) {
      return;
    }

    let info: SysIResultados[] = (await gridRef.current?.instance.getSelectedRowsData()) as SysIResultados[];
    //Sacamos los estudios seleccionados
    const selectedStudies: number[] = [];
    const uniqueMap: { [key: number]: boolean } = {};

    info.forEach((obj) => {
      if (!uniqueMap[obj.Res_Es_Id]) {
        uniqueMap[obj.Res_Es_Id] = true;
        selectedStudies.push(obj.Res_Es_Id);
      }
    });
    if (info.length === 0) {
      newToast('INFO', 'Seleccione al menos un analito para imprimir sus etiquetas seleccionadas', 5);
      return;
    }

    try {
      isPrintingRef.current = true;
      await handlePrintBarcodesLabel(idOrd, selectedStudies);
    } finally {
      setTimeout(() => {
        isPrintingRef.current = false;
      }, 500);
    }
  }, [idOrd, handlePrintBarcodesLabel, Prm_CodBarImprimir]);

  //#endregion

  //#region PREVISUALIZAR
  const handlePreviewResults = useCallback(async () => {
    if (idOrd === -1) {
      return;
    }
    if (!Prm_ResImprimir) {
      newToast('INFO', 'No cuenta con los permisos para imprimir resultados', 5);
      return;
    }
    setModalPreviewResult(true);
  }, [idOrd]);
  //#endregion

  //#region DELETE ANALITO
  const handleDeleteAnalito = useCallback(async () => {
    if (!Prm_Res_DelAn) {
      return;
    }

    try {
      await gridRef.current?.instance.beginCustomLoading('');
      let info: SysIResultados[] = (await gridRef.current?.instance.getSelectedRowsData()) as SysIResultados[];
      if (info.length === 0 || info.length > 1) {
        newToast('INFO', 'Debe seleccionar un único analito a eliminar', 5);
        return;
      }
      //Eliminamos los resultados que no están en estado > 3
      info = info.filter((x) => x.Res_Estado === ResultEstadoResult.SinResultado);
      if (info.length === 0) {
        newToast('INFO', 'El analito seleccionado debe estar en estado "Sin resultado"', 5);
        return;
      }
      setModalConfirmationDeleteanalito(true);
    } finally {
      await gridRef.current?.instance.endCustomLoading();
    }
  }, []);

  const handleDeleteAnalitoCancel = useCallback(() => {
    setModalConfirmationDeleteanalito(false);
  }, []);

  const handleDeleteAnalitoConfirm = useCallback(async () => {
    setModalConfirmationDeleteanalito(false);
    let info: SysIResultados[] = (await gridRef.current?.instance.getSelectedRowsData()) as SysIResultados[];
    if (info.length === 0 || info.length > 1) {
      newToast('INFO', 'Debe seleccionar un único analito a eliminar', 5);
      return;
    }
    //Eliminamos los resultados que no están en estado > 3
    info = info.filter((x) => x.Res_Estado === ResultEstadoResult.SinResultado);
    if (info.length === 0) {
      newToast('INFO', 'El analito seleccionado debe estar en estado "Sin resultado"', 5);
      return;
    }
    await gridRef.current?.instance.beginCustomLoading('');
    await deleteAnalitoResult({ IdEs: info[0].Res_Es_Id, IdAn: info[0].Res_An_Id });
    gridRef.current?.instance.refresh();
    await gridRef.current?.instance.endCustomLoading();
  }, [deleteAnalitoResult]);
  //#endregion

  //#region ENVIAR RESULTADOS POR CORREO

  const handleSendMail = useCallback(async () => {
    if (idOrd === -1) {
      return;
    }
    if (!Prm_Res_Mail) {
      newToast('INFO', 'No cuenta con los permisos para enviar resultados por correo', 5);
      return;
    }
    let info = await gridRef.current?.instance.getDataSource();
    const data = info?.items().flatMap((area: any) => area.items.flatMap((estudio: any) => estudio.items)) as SysIResultados[];

    const infoFiltered = data.filter((x) => x.Res_Estado >= ResultEstadoResult.Validado);
    if (infoFiltered.length === 0) {
      newToast('INFO', 'No hay analitos validados para la orden seleccionada', 5);
      return;
    }
    setModalSendMail({ visible: true, studies: undefined });
  }, [idOrd]);

  const handleSendMailSelected = useCallback(async () => {
    if (idOrd === -1) {
      return;
    }
    if (!Prm_Res_Mail) {
      newToast('INFO', 'No cuenta con los permisos para enviar resultados por correo', 5);
      return;
    }

    try {
      let info: SysIResultados[] = (await gridRef.current?.instance.getSelectedRowsData()) as SysIResultados[];
      //Eliminamos los resultados que no están en estado 2
      info = info.filter((x) => x.Res_Estado >= ResultEstadoResult.Validado);
      if (info.length === 0) {
        newToast('INFO', 'No hay analitos validados para la orden seleccionada', 5);
        return;
      }
      //Sacamos los estudios seleccionados
      const selectedStudies: number[] = [];
      const uniqueMap: { [key: number]: boolean } = {};

      info.forEach((obj) => {
        if (!uniqueMap[obj.Res_Es_Id]) {
          uniqueMap[obj.Res_Es_Id] = true;
          selectedStudies.push(obj.Res_Es_Id);
        }
      });
      setModalSendMail({ visible: true, studies: selectedStudies });
    } finally {
      await gridRef.current?.instance.endCustomLoading();
    }
  }, [idOrd]);

  const handleCloseModalSendMail = useCallback(() => {
    setModalSendMail({ visible: false, studies: undefined });
  }, []);

  //#endregion

  //#region ENVIAR RESULTADOS POR WHATSAPP
  const handleSendWhatsApp = useCallback(async () => {
    if (idOrd === -1) {
      return;
    }
    if (!Prm_Res_WhatsApp) {
      newToast('INFO', 'No cuenta con los permisos para enviar resultados por WhatsApp', 5);
      return;
    }
    let info = await gridRef.current?.instance.getDataSource();
    const data = info?.items().flatMap((area: any) => area.items.flatMap((estudio: any) => estudio.items)) as SysIResultados[];
    //Eliminamos los resultados que no están en estado 2
    const infoFiltered = data.filter((x) => x.Res_Estado >= ResultEstadoResult.Validado);
    if (infoFiltered.length === 0) {
      newToast('INFO', 'No hay analitos validados para la orden seleccionada', 5);
      return;
    }
    setModalSendWhatsApp({ visible: true, studies: undefined });
  }, [idOrd]);

  const handleSendWhatsAppSelected = useCallback(async () => {
    if (idOrd === -1) {
      return;
    }
    if (!Prm_Res_WhatsApp) {
      newToast('INFO', 'No cuenta con los permisos para enviar resultados por WhatsApp', 5);
      return;
    }

    try {
      let info: SysIResultados[] = (await gridRef.current?.instance.getSelectedRowsData()) as SysIResultados[];
      //Eliminamos los resultados que no están en estado 2
      info = info.filter((x) => x.Res_Estado >= ResultEstadoResult.Validado);
      if (info.length === 0) {
        newToast('INFO', 'No hay analitos validados para la orden seleccionada', 5);
        return;
      }
      //Sacamos los estudios seleccionados
      const selectedStudies: number[] = [];
      const uniqueMap: { [key: number]: boolean } = {};

      info.forEach((obj) => {
        if (!uniqueMap[obj.Res_Es_Id]) {
          uniqueMap[obj.Res_Es_Id] = true;
          selectedStudies.push(obj.Res_Es_Id);
        }
      });
      setModalSendWhatsApp({ visible: true, studies: selectedStudies });
    } finally {
      await gridRef.current?.instance.endCustomLoading();
    }
  }, [idOrd]);

  const handleCloseModalSendWhatsApp = useCallback(() => {
    setModalSendWhatsApp({ visible: false, studies: undefined });
  }, []);

  //#endregion

  if (!idOrd || idOrd === -1) {
    return <NoDataPanel title={formatMessage('OrdNoDataTitle')} description={formatMessage('OrdNoDataDescription')} />;
  }

  return (
    <div style={{ marginTop: 5, height: '100%' }}>
      {/* Modal Confirmación resutlados críticos */}
      <ModalConfirmation visible={modalConfirmation.visible} buttons='No/Yes' onConfirm={handleConfirValidateCritical} onCancel={handleCancelValidateCritical} message={modalConfirmation.message} />
      {/* Modal Confirmación para eliminar analitos */}
      <ModalConfirmation visible={modalConfirmationDeleteAnalito} buttons='No/Yes' onConfirm={handleDeleteAnalitoConfirm} onCancel={handleDeleteAnalitoCancel} message={'Está seguro que desea eliminar el analito seleccionado\n\nTenga en cuenta que una vez eliminado este analito, no se prodrá agregar nuevamente.'} />
      {/* Notas de desvalidación */}
      <ModalNotes visible={modalNotePlantillaInvalidation.visible} type='Invalidation' currentNote='' onConfirm={handleNoteValidationConfirm} onCancel={handleNoteValidationCancel} />
      {/* Notas de desvalidacion */}
      <ModalNotes visible={(ModalNotesValidation && ModalNotesValidation?.Visible) || false} type='Validation' state={ModalNotesValidation?.Res_Estado} currentNote={(ModalNotesValidation && ModalNotesValidation.Res_Resultado) || ''} onConfirm={handleConfirmNotesValidation} onCancel={handleCancelNotesValidation} />
      {/* Modales diferidos: montados solo mientras están abiertos (un Suspense por modal para
          que la carga de uno no desmonte a otro que ya esté abierto). Los que leen su estado
          de Redux solo reinician estado local al cerrarse, así que desmontarlos equivale. */}
      {dataModal?.Visible && (
        <Suspense fallback={null}>
          <ModalResultTemplates />
        </Suspense>
      )}
      {modalImagesVisible && (
        <Suspense fallback={null}>
          <ModalResultImages />
        </Suspense>
      )}
      {dataModalMicro?.Visible && (
        <Suspense fallback={null}>
          <ModalMicrobiologyResults />
        </Suspense>
      )}
      {dataModalMultiline?.Visible && (
        <Suspense fallback={null}>
          <ModalResultMultiline />
        </Suspense>
      )}
      {dataModalImagenesDx?.Visible && (
        <Suspense fallback={null}>
          <ModalResultImagenesDx dataModal={dataModalImagenesDx} onClose={handleCloseImagenesDx} />
        </Suspense>
      )}
      {modalOpportunityVisible && (
        <Suspense fallback={null}>
          <ModalOpportunity visible={modalOpportunityVisible} setVisible={handleCloseModalOpportunity} />
        </Suspense>
      )}
      {modalSendMail.visible && (
        <Suspense fallback={null}>
          <SendMailResuls visible={modalSendMail.visible} setVisible={handleCloseModalSendMail} lstStudies={modalSendMail.studies} />
        </Suspense>
      )}
      {modalSendWhatsApp.visible && (
        <Suspense fallback={null}>
          <SendWhatsAppResults visible={modalSendWhatsApp.visible} setVisible={handleCloseModalSendWhatsApp} lstStudies={modalSendWhatsApp.studies} />
        </Suspense>
      )}
      {modalHistoryVisible && (
        <Suspense fallback={null}>
          <ModalResultHistory />
        </Suspense>
      )}
      {modalSampleTubesVisible && (
        <Suspense fallback={null}>
          <ModalSampleTubes visible={modalSampleTubesVisible} setVisible={setModalSampleTubesVisible} />
        </Suspense>
      )}
      {modalPrintResult.visible && (
        <Suspense fallback={null}>
          <ModalReportResults visible={modalPrintResult.visible} setVisible={setModalPrintResult} studies={modalPrintResult.studies} reportName={'Resultados'} idOrd={idOrd} />
        </Suspense>
      )}
      {modalPreviewResult && (
        <Suspense fallback={null}>
          <ModalReportPreview visible={modalPreviewResult} setVisible={setModalPreviewResult} reportName={'ResultadosPreview'} idOrd={idOrd} />
        </Suspense>
      )}
      <div className='toolbar-results'>
        <ResultsToolbar
          selectedStates={selectedStates}
          onSelectAll={handleSelectAll}
          onShowSamples={handleShowSamples}
          onOpportunity={handleOpenModalOpportunity}
          onClear={handleClearResult}
          onPinWhite={handlePinResultWhite}
          onInvalidate={handleOpenModalInvalidation}
          onValidate={handleClickValidateResult}
          onResend={handleReSendResulds}
          onDelete={handleDeleteAnalito}
          onPreview={handlePreviewResults}
          onPrint={handlePrintResults}
          onPrintSelected={handlePrintResultsSelected}
          onSendMail={handleSendMail}
          onSendMailSelected={handleSendMailSelected}
          onSendWhatsApp={handleSendWhatsApp}
          onSendWhatsAppSelected={handleSendWhatsAppSelected}
          onPrintBarcodes={handlePrintBarcode}
          onPrintBarcodesSelected={handlePrintBarcodeSelected}
          onRefresh={updateData}
          onFilterChange={handleSelectedState}
          permisos={{
            Prm_Conf_Campos_Diseno,
            modeEditor,
            Prm_ResValidar,
            Prm_Res_Retransmitir,
            Prm_Res_DelAn,
            Prm_ResImprimir,
            ModuleBarcode,
            ModuleEmail,
            ModuleWhatsApp,
            Prm_Res_Mail,
            Prm_Res_WhatsApp,
          }}
        />
      </div>
      <div id='gridContainer' style={{ height: 'calc(100vh - 300px)', overflow: 'auto', paddingBottom: isSmall || isXSmall ? `150px` : `50px` }}>
        <DataGrid onKeyDown={handleKeys} className='gridResult' height={'100%'} dataSource={customDataSrc} ref={gridRef} width={'100%'} repaintChangesOnly={true} highlightChanges={false} rowAlternationEnabled={false} showColumnLines={true}>
          <Selection mode='multiple' deferred={true} showCheckBoxesMode='none' />
          <Grouping autoExpandAll={true} allowCollapsing={false} />
          <Scrolling mode='standard' showScrollbar='always' useNative={true} scrollByContent={true} />
          <Sorting mode='multiple' />

          <KeyboardNavigation editOnKeyPress={true} enterKeyAction='startEdit' enterKeyDirection='column' />
          <Paging enabled={false} />
          <Editing allowUpdating={true} selectTextOnEditStart={true} mode='cell' />
          <Column allowGrouping={false} allowSorting={false} caption='ID' allowEditing={false} visible={modeEditor} dataField='Res_Id' width={75} alignment='center' />
          <Column allowGrouping={false} allowSorting={false} caption='ID_ANALITO' allowEditing={false} visible={modeEditor} dataField='Res_An_Id' width={75} alignment='center' />
          <Column allowGrouping={false} caption='ID_ÁREA' visible={modeEditor} allowEditing={false} dataField='Res_Aa_Id' width={75} alignment='center' />
          <Column allowGrouping={false} caption='ID_ESTUDIO' visible={modeEditor} allowEditing={false} dataField='Res_Es_Id' width={75} alignment='center' />
          <Column caption='ÁREA' visible={false} allowEditing={false} groupIndex={0} calculateGroupValue={'Area.Aa_OrdenImpresion'} dataField='Area.Aa_Nombre' groupCellRender={handleGroupRowAr} />
          <Column caption='ESTUDIO' visible={false} allowEditing={false} groupIndex={1} calculateGroupValue={'Estudio.Es_OrdenImpresion'} dataField='Estudio.Es_Nombre' groupCellRender={handleGroupRowEs} />
          <Column visible={modeEditor} width={75} showInColumnChooser={false} dataField='Res_OrdenImpresion' sortIndex={2} />
          <Column allowGrouping={false} allowSorting={false} caption='TIPO' cellRender={handleSample} allowEditing={false} dataField='Analito.TipoMuestra.Tm_Color' width={isLarge ? 60 : 45} alignment='right' />
          <Column allowGrouping={false} allowSorting={false} caption='ACCIONES' allowEditing={false} width={isLarge ? 100 : 73} alignment='center' cellRender={handleCombinedActions} />
          {/* <Column allowGrouping={false} allowSorting={false} caption='NOTA' allowEditing={false} dataField='Res_NotaValidacion' width={49} alignment='center' cellRender={handleNote} /> */}
          <Column allowGrouping={false} allowSorting={false} caption='EST.' showInColumnChooser={false} cellRender={handleStatusResult} allowEditing={false} dataField='Res_Estado' width={isLarge ? 60 : 48} alignment='center' />
          <Column allowGrouping={false} allowSorting={false} caption='ANALITO' minWidth={100} showInColumnChooser={false} allowEditing={false} dataField='Analito.An_Nombre' />
          <Column allowGrouping={false} allowSorting={false} caption='TIPO RESULT' width={75} allowEditing={false} visible={modeEditor} showInColumnChooser={false} dataField='Analito.An_Tr_Id' alignment='center' />
          <Column allowGrouping={false} allowSorting={false} caption='RESULTADO' showInColumnChooser={false} dataField='Res_Resultado' allowEditing={true} dataType='object' editCellComponent={DynamicEditCellComponent} cellComponent={CellComponent} alignment='center' width={isLarge ? 200 : 120} />
          <Column allowGrouping={false} allowSorting={false} caption='ANT' allowEditing={false} dataField='Res_ResultadoAnterior' alignment='center' width={isLarge ? 120 : 70} cellRender={handleAnt} />
          <Column allowGrouping={false} allowSorting={false} caption='PREVIO 1' allowEditing={false} dataField='Res_ResultadoPrevio1' alignment='center' width={isLarge ? 120 : 70} cellRender={handlePreview1} />
          <Column allowGrouping={false} allowSorting={false} caption='PREVIO 2' allowEditing={false} dataField='Res_ResultadoPrevio2' alignment='center' width={isLarge ? 120 : 70} cellRender={handlePreview2} />
          <Column allowGrouping={false} allowSorting={false} caption='UNIDAD' allowEditing={false} dataField='Res_UnidadMedida' alignment='center' width={isLarge ? 120 : 70} />
          <Column allowGrouping={false} allowSorting={false} caption='REFERENCIA' allowEditing={false} alignment='center' width={99} cellRender={handleValRef} />
        </DataGrid>
      </div>
    </div>
  );
};

export default Index;
